/**
 * Built-in themes and theme token definitions.
 * Themes are applied via CSS custom properties on the <html> element.
 */

export type ThemeTokens = {
  bg: string;
  surface: string;
  text: string;
  textMuted: string;
  accent: string;
  accentHover: string;
  border: string;
  success: string;
  warning: string;
  error: string;
  fontBody: string;
  fontHeading: string;
};

export type Theme = {
  id: string;
  name: string;
  tokens: ThemeTokens;
};

export const B2W2_DARK: Theme = {
  id: "b2w2-dark",
  name: "B2W2 Dark",
  tokens: {
    bg: "#1a1a2e",
    surface: "#252544",
    text: "#e8e8f0",
    textMuted: "#a0a0c0",
    accent: "#5a8fd8",
    accentHover: "#7aaae8",
    border: "#3a3a5c",
    success: "#5cb85c",
    warning: "#f0ad4e",
    error: "#d9534f",
    fontBody: "'Segoe UI', system-ui, sans-serif",
    fontHeading: "'Segoe UI', system-ui, sans-serif",
  },
};

export const B2W2_LIGHT: Theme = {
  id: "b2w2-light",
  name: "B2W2 Light",
  tokens: {
    bg: "#f5f5f8",
    surface: "#ffffff",
    text: "#1a1a2e",
    textMuted: "#6a6a8a",
    accent: "#3a6fc8",
    accentHover: "#2a5fb8",
    border: "#d0d0e0",
    success: "#4caf50",
    warning: "#ff9800",
    error: "#f44336",
    fontBody: "'Segoe UI', system-ui, sans-serif",
    fontHeading: "'Segoe UI', system-ui, sans-serif",
  },
};

export const HIGH_CONTRAST: Theme = {
  id: "high-contrast",
  name: "High Contrast",
  tokens: {
    bg: "#000000",
    surface: "#1a1a1a",
    text: "#ffffff",
    textMuted: "#cccccc",
    accent: "#ffff00",
    accentHover: "#ffff66",
    border: "#ffffff",
    success: "#00ff00",
    warning: "#ffff00",
    error: "#ff0000",
    fontBody: "monospace",
    fontHeading: "monospace",
  },
};

export const PASTEL: Theme = {
  id: "pastel",
  name: "Pastel",
  tokens: {
    bg: "#f8f1f4",
    surface: "#ffffff",
    text: "#4a4a5a",
    textMuted: "#8a8a9a",
    accent: "#e89ac0",
    accentHover: "#d47aaf",
    border: "#e8e0e5",
    success: "#7bd88a",
    warning: "#f7d070",
    error: "#f28b82",
    fontBody: "'Segoe UI', system-ui, sans-serif",
    fontHeading: "'Segoe UI', system-ui, sans-serif",
  },
};

export const MIDNIGHT: Theme = {
  id: "midnight",
  name: "Midnight",
  tokens: {
    bg: "#0d1117",
    surface: "#161b22",
    text: "#c9d1d9",
    textMuted: "#8b949e",
    accent: "#58a6ff",
    accentHover: "#79c0ff",
    border: "#30363d",
    success: "#3fb950",
    warning: "#d29922",
    error: "#f85149",
    fontBody: "'Segoe UI', system-ui, sans-serif",
    fontHeading: "'Segoe UI', system-ui, sans-serif",
  },
};

export const FOREST: Theme = {
  id: "forest",
  name: "Forest",
  tokens: {
    bg: "#1b2a24",
    surface: "#243830",
    text: "#e7efe9",
    textMuted: "#9db0a0",
    accent: "#78c27a",
    accentHover: "#95d697",
    border: "#3b5445",
    success: "#6ebd70",
    warning: "#d4b856",
    error: "#d97a6e",
    fontBody: "'Segoe UI', system-ui, sans-serif",
    fontHeading: "'Segoe UI', system-ui, sans-serif",
  },
};

export const BUILT_IN_THEMES: Theme[] = [B2W2_DARK, B2W2_LIGHT, HIGH_CONTRAST, PASTEL, MIDNIGHT, FOREST];

/** CSS custom property names corresponding to each theme token. */
export const TOKEN_TO_CSS_VAR: Record<keyof ThemeTokens, string> = {
  bg: "--bg",
  surface: "--surface",
  text: "--text",
  textMuted: "--text-muted",
  accent: "--accent",
  accentHover: "--accent-hover",
  border: "--border",
  success: "--success",
  warning: "--warning",
  error: "--error",
  fontBody: "--font-body",
  fontHeading: "--font-heading",
};